import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag, Search, Plus, Minus, Trash2, ShoppingCart, X,
  Phone, MapPin, User, Truck, CheckCircle, AlertCircle, Loader, Store,
  Package, Star, Mail, Info, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import './Shop.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';

// Grille principale : nombre de produits par page
const PRODUITS_PAR_PAGE = 24;

// Numéros de pages à afficher : 1 … 4 5 [6] 7 8 … 20
const pagesAffichees = (page, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const res = [1];
  const debut = Math.max(2, page - 2), fin = Math.min(total - 1, page + 2);
  if (debut > 2) res.push('…');
  for (let i = debut; i <= fin; i++) res.push(i);
  if (fin < total - 1) res.push('…');
  res.push(total);
  return res;
};

export default function Shop() {
  const { id } = useParams();

  const [boutique, setBoutique]   = useState(null);
  const [produits, setProduits]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const grilleRef                 = useRef(null);
  const pageCible                 = useRef(null);  // page à afficher après effacement de la recherche
  const [highlightedProductId, setHighlightedProductId] = useState(null);
  const [cart, setCart]           = useState([]);
  const [showCart, setShowCart]   = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const [client, setClient]       = useState({ nom: '', telephone: '', email: '', adresse: '', commentaire: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Détail produit (fenêtre) : produit affiché + quantité à ajouter
  const [detail, setDetail]       = useState(null);
  const [detailQte, setDetailQte] = useState(1);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async (silent = false) => {
    if (!silent) { setLoading(true); setError(''); }
    try {
      // Cache-buster pour s'assurer qu'on n'a pas une vieille version mise en cache
      const t = Date.now();
      const [bRes, pRes] = await Promise.all([
        axios.get(`/api/public/boutiques/${id}`,           { params: { _t: t }, headers: { 'Cache-Control': 'no-cache' } }),
        axios.get(`/api/public/boutiques/${id}/produits`,  { params: { _t: t }, headers: { 'Cache-Control': 'no-cache' } }),
      ]);
      setBoutique(bRes.data.data);
      setProduits(pRes.data.data || []);
    } catch (e) {
      if (!silent) setError(e.response?.data?.message || 'Boutique introuvable');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const produitsFiltres = useMemo(() => {
    let r = produits;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(p =>
        (p.nom || '').toLowerCase().includes(q) ||
        (p.codeProduit || '').toLowerCase().includes(q) ||
        (p.codeBarres || '').toLowerCase().includes(q));
    }
    return r;
  }, [produits, search]);

  // Pagination de la grille principale (retour en page 1 à chaque recherche)
  const nbPages = Math.max(1, Math.ceil(produitsFiltres.length / PRODUITS_PAR_PAGE));
  useEffect(() => {
    setPage(pageCible.current || 1);
    pageCible.current = null;
  }, [search]);
  useEffect(() => { if (page > nbPages) setPage(nbPages); }, [page, nbPages]);
  const produitsPage = useMemo(
    () => produitsFiltres.slice((page - 1) * PRODUITS_PAR_PAGE, page * PRODUITS_PAR_PAGE),
    [produitsFiltres, page]
  );
  const allerPage = (n) => {
    setPage(Math.min(Math.max(1, n), nbPages));
    setTimeout(() => grilleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const addToCart = (p, overridePrice, qte = 1) => {
    setCart(c => {
      const idx = c.findIndex(i => i.produit === p._id);
      if (idx >= 0) {
        const newQ = Math.min(c[idx].quantite + qte, p.quantiteStock);
        if (newQ <= c[idx].quantite) return c;
        return c.map((i, k) => k === idx ? { ...i, quantite: newQ } : i);
      }
      return [...c, {
        produit: p._id,
        nom: p.nom,
        prix: overridePrice !== undefined ? overridePrice : (p.prixVenteDetail || p.prixVente || 0),
        quantite: Math.max(1, Math.min(qte, p.quantiteStock)),
        stock: p.quantiteStock,
      }];
    });
  };

  // Ouvre le détail ; préfère la fiche complète du catalogue (description, champs…)
  const ouvrirDetail = (p) => {
    setDetail(produits.find(x => String(x._id) === String(p._id)) || p);
    setDetailQte(1);
  };

  useEffect(() => {
    if (!detail) return;
    const onKey = (e) => { if (e.key === 'Escape') setDetail(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail]);

  const updateQty = (id, q) => {
    setCart(c => c.map(i => {
      if (i.produit !== id) return i;
      const nq = Math.max(1, Math.min(i.stock, parseInt(q) || 1));
      return { ...i, quantite: nq };
    }));
  };

  const removeFromCart = (id) => setCart(c => c.filter(i => i.produit !== id));
  const total = cart.reduce((s, i) => s + i.prix * i.quantite, 0);

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!client.nom.trim() || !client.telephone.trim() || !client.adresse.trim()) {
      alert('Veuillez renseigner votre nom, téléphone et adresse de livraison.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`/api/public/boutiques/${id}/commande`, {
        client,
        items: cart.map(i => ({ produit: i.produit, quantite: i.quantite })),
        commentaire: client.commentaire,
      });
      setSuccessData(data.data);
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="shop-loading">
      <Loader size={26} className="shop-spin"/> Chargement de la boutique…
    </div>
  );
  if (error) return (
    <div className="shop-error">
      <AlertCircle size={28}/>
      <p>{error}</p>
    </div>
  );

  const cfg = boutique?.vitrineConfig || {};
  const design = cfg.design || 'classic';
  const layout = cfg.layout || 'grid';
  const accent = cfg.primaryColor || '#6366f1';
  const ctaLabel = cfg.ctaLabel || 'Ajouter';
  const showStock = cfg.showStock !== false;
  // Prix masqués : « prix communiqué à la confirmation » (le serveur ne les envoie pas)
  const showPrice = cfg.showPrice !== false;
  const showSearch = cfg.showSearch !== false;
  const showProductGrid = cfg.showProductGrid !== false;
  const productGridTitle = cfg.productGridTitle || 'Tous nos produits';
  const showProductGridTitle = cfg.showProductGridTitle !== false && !!productGridTitle.trim();
  const sections = (cfg.sections || []).filter(s => s.enabled !== false);
  const hasHero = sections.some(s => s.type === 'hero');

  // ── Promotions : retourne { price, original, discount, promo } pour un produit ──
  const promotions = cfg.promotions || [];
  const computePrice = (p) => {
    const original = p.prixVenteDetail || p.prixVente || 0;
    const now = Date.now();
    const applicable = promotions.find(pr => {
      if (!pr || !pr.active) return false;
      if (pr.dateDebut && new Date(pr.dateDebut).getTime() > now) return false;
      if (pr.dateFin   && new Date(pr.dateFin).getTime()   < now) return false;
      if (pr.targetType === 'all') return true;
      const ids = (pr.targetIds || []).map(String);
      if (pr.targetType === 'products') return ids.includes(String(p._id));
      return false;
    });
    if (!applicable) return { price: original, original, discount: 0, promo: null };
    const discount = applicable.type === 'percent'
      ? Math.round((original * (applicable.value || 0)) / 100)
      : Math.round(applicable.value || 0);
    const price = Math.max(0, original - discount);
    return { price, original, discount, promo: applicable };
  };

  // Scroll vers un produit et le surligner
  const focusProduct = (productId) => {
    setSearch('');
    // Afficher la page de la grille qui contient ce produit (liste complète, sans recherche)
    const idx = produits.findIndex(p => String(p._id) === String(productId));
    if (idx >= 0) {
      const cible = Math.floor(idx / PRODUITS_PAR_PAGE) + 1;
      if (search) pageCible.current = cible;   // appliquée par l'effet de la recherche
      else setPage(cible);
    }
    setHighlightedProductId(productId);
    setTimeout(() => {
      const el = document.querySelector(`[data-product-id="${productId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    setTimeout(() => setHighlightedProductId(null), 3500);
  };

  return (
    <div className={`shop-page shop-design-${design} shop-layout-${layout}`} style={{ '--accent': accent }}>
      {/* Header */}
      <header className="shop-header">
        <div className="shop-header-inner">
          <div className="shop-brand">
            <div className="shop-brand-icon"><Store size={20}/></div>
            <div>
              <h1 className="shop-brand-name">{boutique?.nom}</h1>
              <div className="shop-brand-meta">
                {boutique?.adresse && <span><MapPin size={11}/> {boutique.adresse}</span>}
                {boutique?.telephone && <span><Phone size={11}/> {boutique.telephone}</span>}
              </div>
            </div>
          </div>
          <button className="shop-refresh-btn" onClick={() => load()} title="Recharger la vitrine">
            <RefreshCw size={14}/>
          </button>
          <button className="shop-cart-btn" onClick={() => setShowCart(true)}>
            <ShoppingCart size={16}/>
            <span>Panier</span>
            {cart.length > 0 && <span className="shop-cart-badge">{cart.length}</span>}
          </button>
        </div>
      </header>

      {/* Si l'admin n'a placé aucun "hero", on affiche un bandeau minimal "paiement à la livraison" */}
      {!hasHero && (
        <div className="shop-mini-banner">
          <Truck size={14}/>
          <span>Commandez en ligne — paiement à la livraison</span>
        </div>
      )}

      {/* Toutes les sections sont pilotées par l'admin */}
      {sections.map((s, idx) => {
        const c = s.config || {};
        if (s.type === 'hero') {
          const align = c.align || 'center';
          const heroStyle = {
            backgroundImage: c.imageUrl ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.45)), url("${c.imageUrl}")` : `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 60%, #000 40%) 100%)`,
            minHeight: (c.height || 'medium') === 'large' ? 420 : (c.height === 'small' ? 220 : 320),
            textAlign: align,
          };
          return (
            <section key={s.id || idx} className={`shop-hero-full shop-hero-align-${align}`} style={heroStyle}>
              <div className="shop-hero-content">
                {c.tag && <span className="shop-hero-tag">{c.tag}</span>}
                <h1 className="shop-hero-title">{c.title || 'Bienvenue'}</h1>
                {c.subtitle && <p className="shop-hero-subtitle">{c.subtitle}</p>}
                {c.ctaLabel && (
                  <button className="shop-hero-cta" style={{ background: accent }}
                    onClick={() => { window.scrollTo({ top: 600, behavior: 'smooth' }); }}>
                    {c.ctaLabel}
                  </button>
                )}
              </div>
            </section>
          );
        }
        if (s.type === 'banner') {
          return (
            <div key={s.id || idx} className="shop-sec shop-sec-banner" style={{ background: c.bgColor || '#fff8ec', color: c.textColor || '#92400e' }}>
              <div className="shop-sec-banner-inner">
                {c.icon && <span className="shop-sec-banner-ico">{c.icon}</span>}
                <span>{c.title ? <><strong>{c.title}</strong>{c.text ? ` — ${c.text}` : ''}</> : c.text}</span>
              </div>
            </div>
          );
        }
        if (s.type === 'featured') {
          const items = c.produits || [];
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-featured">
              <div className="shop-sec-head">
                <h3>{c.title || 'Produits phares'}</h3>
                {c.subtitle && <p>{c.subtitle}</p>}
              </div>
              {items.length === 0 ? (
                <div className="shop-sec-empty">Aucun produit phare sélectionné.</div>
              ) : (
                <div className="shop-featured-grid">
                  {items.map(p => {
                    const inCart = cart.find(i => i.produit === p._id);
                    const { price, original, discount, promo } = computePrice(p);
                    const hasPromo = promo && discount > 0;
                    return (
                      <div key={p._id} className="shop-featured-card">
                        <div className="shop-featured-img shop-clickable" onClick={() => ouvrirDetail(p)} title="Voir le détail">
                          {p.image ? <img src={p.image} alt={p.nom}/> : <ShoppingBag size={32}/>}
                          {hasPromo && showPrice ? (
                            <span className="shop-card-promo-badge">
                              {promo.type === 'percent' ? `−${promo.value}%` : `−${fmt(discount)}`}
                            </span>
                          ) : (
                            <span className="shop-featured-badge">⭐ Phare</span>
                          )}
                        </div>
                        <div className="shop-featured-body">
                          <div className="shop-featured-name shop-clickable" title={p.nom} onClick={() => ouvrirDetail(p)}>{p.nom}</div>
                          {p.codeProduit && <div className="shop-card-ref">Réf. {p.codeProduit}</div>}
                          <div className="shop-featured-foot">
                            {showPrice ? (
                              <span className="shop-featured-price">
                                {hasPromo && <span className="shop-card-price-old">{fmt(original)}</span>}
                                {fmt(price)}
                              </span>
                            ) : <span className="shop-price-hidden">Prix sur demande</span>}
                            {inCart ? (
                              <div className="shop-card-qty">
                                <button onClick={() => updateQty(p._id, inCart.quantite - 1)}><Minus size={11}/></button>
                                <span>{inCart.quantite}</span>
                                <button onClick={() => updateQty(p._id, inCart.quantite + 1)} disabled={inCart.quantite >= p.quantiteStock}><Plus size={11}/></button>
                              </div>
                            ) : (
                              <button className="shop-add-btn" onClick={() => addToCart(p, price)}>
                                <Plus size={12}/> {ctaLabel}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        }
        if (s.type === 'categories') {
          const items = c.items || [];
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-categories">
              <div className="shop-sec-head">
                <h3>{c.title || 'Catégories'}</h3>
              </div>
              <div className="shop-cat-chips">
                {items.map((it, i) => (
                  <button key={i} className="shop-cat-chip"
                    onClick={() => { setSearch(it.query || it.label); window.scrollTo({ top: 600, behavior: 'smooth' }); }}>
                    {it.label}
                  </button>
                ))}
              </div>
            </section>
          );
        }
        if (s.type === 'testimonials') {
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-testimonials">
              <div className="shop-sec-head">
                <h3>{c.title || 'Témoignages'}</h3>
              </div>
              <div className="shop-testi-grid">
                {(c.items || []).map((t, i) => (
                  <div key={i} className="shop-testi-card">
                    <div className="shop-testi-stars">
                      {Array.from({ length: Math.min(5, Math.max(1, t.rating || 5)) }).map((_, k) => (
                        <Star key={k} size={13} fill="#fca311" stroke="#fca311"/>
                      ))}
                    </div>
                    <p className="shop-testi-text">"{t.text}"</p>
                    <div className="shop-testi-author">
                      <strong>{t.author || 'Client'}</strong>
                      {t.role && <span> — {t.role}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        if (s.type === 'advantages') {
          const items = c.items || [];
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-advantages">
              {c.title && <div className="shop-sec-head"><h3>{c.title}</h3></div>}
              <div className="shop-advantages-grid">
                {items.map((it, i) => (
                  <div key={i} className="shop-adv-card">
                    <div className="shop-adv-icon">{it.icon || '✨'}</div>
                    <div className="shop-adv-title">{it.title}</div>
                    <div className="shop-adv-text">{it.text}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        if (s.type === 'stats') {
          const items = c.items || [];
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-stats">
              {c.title && <div className="shop-sec-head"><h3>{c.title}</h3></div>}
              <div className="shop-stats-grid">
                {items.map((it, i) => (
                  <div key={i} className="shop-stat-card">
                    <div className="shop-stat-value" style={{ color: accent }}>{it.value}</div>
                    <div className="shop-stat-label">{it.label}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        if (s.type === 'split') {
          const pos = c.imagePosition || 'right';
          const handleSplitCta = () => {
            if (c.linkType === 'product' && c.linkId) {
              focusProduct(c.linkId);
              return;
            }
            const el = document.querySelector('.shop-grid, .shop-grid-title-wrap');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          };
          return (
            <section key={s.id || idx} className={`shop-sec shop-sec-split shop-split-${pos}`}>
              <div className="shop-split-inner">
                <div className="shop-split-text">
                  {c.title && <h3>{c.title}</h3>}
                  {c.subtitle && <p className="shop-split-subtitle">{c.subtitle}</p>}
                  {c.text && <p className="shop-split-body">{c.text}</p>}
                  {c.ctaLabel && (
                    <button className="shop-split-cta" style={{ background: accent }} onClick={handleSplitCta}>
                      {c.ctaLabel} →
                    </button>
                  )}
                </div>
                <div className="shop-split-image"
                  style={{ backgroundImage: c.imageUrl ? `url("${c.imageUrl}")` : `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 50%, #000 50%))` }}/>
              </div>
            </section>
          );
        }
        if (s.type === 'gallery') {
          const images = (c.images || []).filter(Boolean);
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-gallery">
              {(c.title || c.subtitle) && (
                <div className="shop-sec-head">
                  {c.title && <h3>{c.title}</h3>}
                  {c.subtitle && <p>{c.subtitle}</p>}
                </div>
              )}
              {images.length === 0 ? (
                <div className="shop-sec-empty">Ajoutez des URLs d'images côté admin.</div>
              ) : (
                <div className="shop-gallery-grid">
                  {images.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="shop-gallery-item"
                      style={{ backgroundImage: `url("${url}")` }}>
                      <span className="shop-gallery-overlay"/>
                    </a>
                  ))}
                </div>
              )}
            </section>
          );
        }
        if (s.type === 'faq') {
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-faq">
              {c.title && <div className="shop-sec-head"><h3>{c.title}</h3></div>}
              <div className="shop-faq-list">
                {(c.items || []).map((it, i) => (
                  <details key={i} className="shop-faq-item">
                    <summary>{it.question}</summary>
                    <p>{it.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          );
        }
        if (s.type === 'about') {
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-about">
              <div className="shop-sec-about-inner">
                <div className="shop-sec-about-ico"><Info size={22}/></div>
                <div>
                  <h3>{c.title || 'À propos'}</h3>
                  <p>{c.text || ''}</p>
                </div>
              </div>
            </section>
          );
        }
        if (s.type === 'newsletter') {
          return (
            <section key={s.id || idx} className="shop-sec shop-sec-newsletter">
              <div className="shop-sec-newsletter-inner">
                <Mail size={26}/>
                <div className="shop-sec-newsletter-text">
                  <h3>{c.title || 'Restez informé'}</h3>
                  <p>{c.subtitle || ''}</p>
                </div>
                <form className="shop-newsletter-form" onSubmit={(e) => { e.preventDefault(); alert('Merci ! Vous êtes inscrit.'); }}>
                  <input type="email" required placeholder={c.placeholder || 'votre@email.com'}/>
                  <button type="submit">{c.ctaLabel || 'S\'inscrire'}</button>
                </form>
              </div>
            </section>
          );
        }
        return null;
      })}

      {/* Search */}
      {showSearch && showProductGrid && (
        <div className="shop-search-wrap">
          <div className="shop-search">
            <Search size={15}/>
            <input
              type="text"
              placeholder="Rechercher un produit (nom, code, code-barres)…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button onClick={() => setSearch('')}><X size={13}/></button>}
          </div>
          <div className="shop-result-count">
            {produitsFiltres.length} produit{produitsFiltres.length > 1 ? 's' : ''} disponible{produitsFiltres.length > 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Titre de la grille — toggleable */}
      {showProductGrid && showProductGridTitle && (
        <div className="shop-grid-title-wrap">
          <h2 className="shop-grid-title">{productGridTitle}</h2>
          <span className="shop-grid-count">{produitsFiltres.length} produit{produitsFiltres.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Grid principale — peut être masquée par l'admin */}
      {showProductGrid && <div ref={grilleRef} className="shop-grid-anchor"/>}
      {showProductGrid && (
      <div className="shop-grid">
        {produitsFiltres.length === 0 ? (
          <div className="shop-empty">
            <Package size={36}/>
            <p>Aucun produit disponible{search ? ' pour cette recherche' : ''}.</p>
          </div>
        ) : produitsPage.map(p => {
          const inCart = cart.find(i => i.produit === p._id);
          const { price, original, discount, promo } = computePrice(p);
          const hasPromo = promo && discount > 0;
          const isHighlighted = String(p._id) === String(highlightedProductId);
          return (
            <div key={p._id} data-product-id={p._id}
              className={`shop-card ${isHighlighted ? 'shop-card--highlighted' : ''} ${hasPromo ? 'shop-card--promo' : ''}`}>
              <div className="shop-card-image shop-clickable" onClick={() => ouvrirDetail(p)} title="Voir le détail">
                {p.image ? <img src={p.image} alt={p.nom}/> : <ShoppingBag size={32}/>}
                {hasPromo && showPrice && (
                  <span className="shop-card-promo-badge">
                    {promo.type === 'percent' ? `−${promo.value}%` : `−${fmt(discount)}`}
                  </span>
                )}
                {showStock && p.quantiteStock <= 5 && <span className="shop-card-low">Stock limité</span>}
              </div>
              <div className="shop-card-body">
                <div className="shop-card-name shop-clickable" title={p.nom} onClick={() => ouvrirDetail(p)}>{p.nom}</div>
                {p.codeProduit && <div className="shop-card-ref">Réf. {p.codeProduit}</div>}
                <div className="shop-card-foot">
                  {showPrice ? (
                    <div className="shop-card-price">
                      {hasPromo && <span className="shop-card-price-old">{fmt(original)}</span>}
                      {fmt(price)}
                    </div>
                  ) : <div className="shop-price-hidden">Prix sur demande</div>}
                  {inCart ? (
                    <div className="shop-card-qty">
                      <button onClick={() => updateQty(p._id, inCart.quantite - 1)}>
                        <Minus size={12}/>
                      </button>
                      <span>{inCart.quantite}</span>
                      <button onClick={() => updateQty(p._id, inCart.quantite + 1)} disabled={inCart.quantite >= p.quantiteStock}>
                        <Plus size={12}/>
                      </button>
                    </div>
                  ) : (
                    <button className="shop-add-btn" onClick={() => addToCart(p, price)}>
                      <Plus size={13}/> {ctaLabel}
                    </button>
                  )}
                </div>
                {showStock && <div className="shop-card-stock">Stock {p.quantiteStock}</div>}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Pagination de la grille principale */}
      {showProductGrid && nbPages > 1 && (
        <nav className="shop-pagination" aria-label="Pagination des produits">
          <button className="shop-page-btn" onClick={() => allerPage(page - 1)} disabled={page <= 1} aria-label="Page précédente">
            <ChevronLeft size={15}/> <span className="shop-page-lbl">Précédent</span>
          </button>
          <div className="shop-page-nums">
            {pagesAffichees(page, nbPages).map((n, i) => n === '…'
              ? <span key={`e${i}`} className="shop-page-gap">…</span>
              : <button key={n} className={`shop-page-num${n === page ? ' shop-page-num--active' : ''}`}
                  aria-current={n === page ? 'page' : undefined}
                  onClick={() => allerPage(n)}>{n}</button>
            )}
          </div>
          <button className="shop-page-btn" onClick={() => allerPage(page + 1)} disabled={page >= nbPages} aria-label="Page suivante">
            <span className="shop-page-lbl">Suivant</span> <ChevronRight size={15}/>
          </button>
          <div className="shop-page-info">
            {(page - 1) * PRODUITS_PAR_PAGE + 1}–{Math.min(page * PRODUITS_PAR_PAGE, produitsFiltres.length)} sur {produitsFiltres.length}
          </div>
        </nav>
      )}

      {/* Cart drawer */}
      {showCart && (
        <>
          <div className="shop-overlay" onClick={() => setShowCart(false)}/>
          <aside className="shop-cart">
            <div className="shop-cart-head">
              <h3><ShoppingCart size={16}/> Mon panier</h3>
              <button onClick={() => setShowCart(false)}><X size={18}/></button>
            </div>

            {cart.length === 0 ? (
              <div className="shop-cart-empty">
                <ShoppingCart size={32}/>
                <p>Votre panier est vide.</p>
              </div>
            ) : (
              <>
                <div className="shop-cart-list">
                  {cart.map(i => (
                    <div key={i.produit} className="shop-cart-item">
                      <div className="shop-cart-item-info">
                        <div className="shop-cart-item-name">{i.nom}</div>
                        <div className="shop-cart-item-price">{showPrice ? `${fmt(i.prix)} × ${i.quantite}` : `Quantité : ${i.quantite}`}</div>
                      </div>
                      <div className="shop-cart-item-qty">
                        <button onClick={() => updateQty(i.produit, i.quantite - 1)}><Minus size={10}/></button>
                        <input type="number" min="1" value={i.quantite} onChange={e => updateQty(i.produit, e.target.value)}/>
                        <button onClick={() => updateQty(i.produit, i.quantite + 1)} disabled={i.quantite >= i.stock}><Plus size={10}/></button>
                      </div>
                      <button className="shop-cart-item-del" onClick={() => removeFromCart(i.produit)}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="shop-cart-foot">
                  {showPrice ? (
                    <div className="shop-cart-total">
                      <span>Total</span>
                      <strong>{fmt(total)}</strong>
                    </div>
                  ) : (
                    <div className="shop-checkout-hint">Prix communiqué par la boutique à la confirmation de la commande</div>
                  )}
                  <button className="shop-checkout-btn" onClick={() => { setShowCheckout(true); setShowCart(false); }}>
                    <Truck size={14}/> Commander{showPrice ? ` · ${fmt(total)}` : ''}
                  </button>
                  <div className="shop-checkout-hint">
                    <CheckCircle size={12}/> Paiement à la livraison — aucun paiement en ligne requis
                  </div>
                </div>
              </>
            )}
          </aside>
        </>
      )}

      {/* Checkout */}
      {showCheckout && (
        <>
          <div className="shop-overlay" onClick={() => !submitting && setShowCheckout(false)}/>
          <div className="shop-checkout-modal">
            <div className="shop-checkout-head">
              <div>
                <h3><Truck size={16}/> Finaliser la commande</h3>
                <p>Paiement à la livraison · {boutique?.nom}</p>
              </div>
              <button onClick={() => !submitting && setShowCheckout(false)} disabled={submitting}><X size={18}/></button>
            </div>

            <form onSubmit={submitOrder} className="shop-checkout-body">
              <div className="shop-section-label"><User size={12}/> Vos informations</div>
              <div className="shop-field">
                <label>Nom complet *</label>
                <input type="text" required value={client.nom}
                  onChange={e => setClient({ ...client, nom: e.target.value })}
                  placeholder="Ex : Mamadou Diop"/>
              </div>
              <div className="shop-form-row">
                <div className="shop-field">
                  <label>Téléphone *</label>
                  <input type="tel" required value={client.telephone}
                    onChange={e => setClient({ ...client, telephone: e.target.value })}
                    placeholder="+221 77 ..."/>
                </div>
                <div className="shop-field">
                  <label>Email (optionnel)</label>
                  <input type="email" value={client.email}
                    onChange={e => setClient({ ...client, email: e.target.value })}
                    placeholder="email@exemple.com"/>
                </div>
              </div>

              <div className="shop-section-label"><MapPin size={12}/> Adresse de livraison</div>
              <div className="shop-field">
                <label>Adresse complète *</label>
                <textarea required rows="2" value={client.adresse}
                  onChange={e => setClient({ ...client, adresse: e.target.value })}
                  placeholder="Quartier, rue, repère..."/>
              </div>
              <div className="shop-field">
                <label>Note pour le livreur (optionnel)</label>
                <textarea rows="2" value={client.commentaire}
                  onChange={e => setClient({ ...client, commentaire: e.target.value })}
                  placeholder="Heure préférée, étage, instructions..."/>
              </div>

              <div className="shop-recap">
                <div className="shop-recap-head">Récapitulatif ({cart.length} article{cart.length > 1 ? 's' : ''})</div>
                {cart.map(i => (
                  <div key={i.produit} className="shop-recap-line">
                    <span>{i.nom} × {i.quantite}</span>
                    {showPrice && <span>{fmt(i.prix * i.quantite)}</span>}
                  </div>
                ))}
                {showPrice ? (
                  <div className="shop-recap-total">
                    <span>Total à régler à la livraison</span>
                    <strong>{fmt(total)}</strong>
                  </div>
                ) : (
                  <div className="shop-recap-total">
                    <span>Prix communiqué à la confirmation — paiement à la livraison</span>
                  </div>
                )}
              </div>

              <button type="submit" className="shop-submit-btn" disabled={submitting || cart.length === 0}>
                {submitting
                  ? <><Loader size={14} className="shop-spin"/> Envoi…</>
                  : <><CheckCircle size={14}/> Valider la commande</>}
              </button>
            </form>
          </div>
        </>
      )}

      {/* Détail produit */}
      {detail && (() => {
        const p = detail;
        const { price, original, discount, promo } = computePrice(p);
        const hasPromo = promo && discount > 0 && showPrice;
        const stock = Number(p.quantiteStock) || 0;
        const inCart = cart.find(i => i.produit === p._id);
        const restant = Math.max(0, stock - (inCart?.quantite || 0));
        const champs = (p.champsPersonnalises || []).filter(c => c && c.cle);
        return (
          <>
            <div className="shop-overlay" onClick={() => setDetail(null)}/>
            <div className="shop-detail-modal" role="dialog" aria-modal="true" aria-label={p.nom}>
              <button className="shop-detail-close" onClick={() => setDetail(null)} aria-label="Fermer"><X size={16}/></button>
              <div className="shop-detail-img">
                {p.image ? <img src={p.image} alt={p.nom}/> : <ShoppingBag size={48}/>}
                {hasPromo && (
                  <span className="shop-card-promo-badge">
                    {promo.type === 'percent' ? `−${promo.value}%` : `−${fmt(discount)}`}
                  </span>
                )}
              </div>
              <div className="shop-detail-body">
                <h3 className="shop-detail-nom">{p.nom}</h3>
                {p.codeProduit && <div className="shop-detail-code">Réf. {p.codeProduit}</div>}

                <div className="shop-detail-price">
                  {showPrice ? (
                    <>
                      {hasPromo && <span className="shop-card-price-old">{fmt(original)}</span>}
                      <strong style={{ color: accent }}>{fmt(price)}</strong>
                    </>
                  ) : <span className="shop-price-hidden">Prix sur demande</span>}
                  {showStock && (
                    <span className={`shop-detail-stock${stock <= 5 ? ' shop-detail-stock--low' : ''}`}>
                      {stock <= 5 ? `Plus que ${stock} en stock` : `${stock} en stock`}
                    </span>
                  )}
                </div>

                {p.description && <p className="shop-detail-desc">{p.description}</p>}

                {champs.length > 0 && (
                  <dl className="shop-detail-champs">
                    {champs.map((c, i) => (
                      <div key={i} className="shop-detail-champ">
                        <dt>{c.cle}</dt><dd>{c.valeur || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                <div className="shop-detail-actions">
                  <div className="shop-card-qty">
                    <button onClick={() => setDetailQte(q => Math.max(1, q - 1))} disabled={detailQte <= 1}><Minus size={12}/></button>
                    <span>{detailQte}</span>
                    <button onClick={() => setDetailQte(q => Math.min(restant || 1, q + 1))} disabled={detailQte >= restant}><Plus size={12}/></button>
                  </div>
                  <button className="shop-add-btn shop-detail-add" disabled={restant <= 0}
                    style={{ background: accent }}
                    onClick={() => { addToCart(p, price, detailQte); setDetail(null); }}>
                    <Plus size={14}/> {restant <= 0 ? 'Quantité max. dans le panier' : ctaLabel}
                  </button>
                </div>
                {inCart && <div className="shop-detail-incart"><CheckCircle size={12}/> Déjà {inCart.quantite} dans le panier</div>}
              </div>
            </div>
          </>
        );
      })()}

      {/* Success */}
      {successData && (
        <>
          <div className="shop-overlay"/>
          <div className="shop-success-modal">
            <div className="shop-success-ico"><CheckCircle size={36}/></div>
            <h3>Commande enregistrée !</h3>
            <p>Votre numéro de commande :</p>
            <div className="shop-success-num">{successData.numeroCommande}</div>
            <p className="shop-success-msg">
              La boutique vous contactera très bientôt pour confirmer la livraison.<br/>
              {showPrice
                ? <strong>Total à régler à la livraison : {fmt(successData.total)}</strong>
                : <strong>Le prix vous sera communiqué à la confirmation — paiement à la livraison.</strong>}
            </p>
            <button className="shop-success-btn" onClick={() => setSuccessData(null)}>
              Continuer mes achats
            </button>
          </div>
        </>
      )}
    </div>
  );
}
